import { Box } from "@outschool/backpack";
import { CurrencyCode } from "@outschool/gql-frontend-generated";
import {
  renderCurrencyStringWithCurrencyConversion,
  useTranslation,
} from "@outschool/localization";
import { TrackedButton } from "@outschool/ui-components-shared";
import React from "react";

import * as Env from "../../../shared/Env";
import ErrorMessage from "../ErrorMessage";
import LoadingWithMessage from "../LoadingWithMessage";
import SuccessWithMessage from "../SuccessWithMessage";

function StripeInProgressMessage() {
  const { t } = useTranslation("client\\components\\payments\\StripeMessages");
  return (
    <>
      {t(
        "Please wait while we process your credit card payment. Please DO NOT close \
this screen or hit the back button.",
      )}
    </>
  );
}

export function StripeProcessingMessage() {
  return (
    <LoadingWithMessage>
      <StripeInProgressMessage />
    </LoadingWithMessage>
  );
}

export function StripeSuccessMessage() {
  return (
    <SuccessWithMessage>
      <StripeInProgressMessage />
    </SuccessWithMessage>
  );
}

interface StripePayMessageProps {
  paymentError?: string | null;
  amountCents: number;
  isChargingInLocalCurrency?: boolean;
  disableSubmit?: boolean;
  exchangeRate: number;
  currencyCode: CurrencyCode;
  handleGoBack: () => void;
}

export function StripePayMessage({
  paymentError,
  amountCents,
  isChargingInLocalCurrency,
  disableSubmit,
  exchangeRate,
  currencyCode,
  handleGoBack,
}: StripePayMessageProps) {
  const { t } = useTranslation("client\\components\\payments\\StripeMessages");

  const priceString = renderCurrencyStringWithCurrencyConversion({
    amountUSDCents: amountCents,
    showCurrencyCode: true,
    ...(isChargingInLocalCurrency
      ? {
          exchangeRate,
          currencyCode,
        }
      : // default to USD
        {}),
  });
  return (
    <>
      <Box
        sx={{
          marginTop: "1em",
        }}
      >
        {paymentError && (
          <Box
            data-test-id="paymentError"
            sx={{
              marginBottom: "1em",
              width: "100%",
            }}
          >
            <ErrorMessage
              value={paymentError}
              showStatusPageLink={Env.IS_READ_ONLY_MODE}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          marginBottom: "1em",
          width: "100%",
        }}
      >
        <TrackedButton
          variant="contained"
          data-test-id="payButton"
          disabled={disableSubmit}
          type="submit"
          trackingName="stripe_pay_button"
          trackingUniqueId="stripe_pay_button"
          sx={{
            width: "100%",
          }}
        >
          {t("Pay {{price}}", { price: priceString })}
        </TrackedButton>
      </Box>
      <Box
        sx={{
          marginBottom: "1em",
          width: "100%",
        }}
      >
        <TrackedButton
          variant="link"
          onClick={handleGoBack}
          sx={{
            width: "100%",
          }}
          trackingName="stripe_pay_go_back_button"
          trackingUniqueId="stripe_pay_go_back_button"
        >
          {t("Go Back")}
        </TrackedButton>
      </Box>
    </>
  );
}
